import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useShop } from "@/store/ShopContext";
import { useAuth } from "@/store/AuthContext";

interface SubNavItem {
  label: string;
  to: string;
  icon?: string;
}

const subNav: SubNavItem[] = [
  { label: "Início", to: "/", icon: "ri-home-4-line" },
  { label: "Nossa História", to: "/story", icon: "ri-book-open-line" },
  { label: "Ajuda", to: "/help", icon: "ri-question-line" },
];

export default function Navbar() {
  const [search, setSearch] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { cartCount, openCart, favorites, showToast } = useShop();
  const { user, signOut } = useAuth();
  const favCount = favorites.length;

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  useEffect(() => {
    if (!accountOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [accountOpen]);

  const initial = user
    ? ((user.user_metadata?.full_name as string | undefined) || user.email || "C")[0]?.toUpperCase()
    : "C";
  const displayName =
    user &&
    ((user.user_metadata?.full_name as string | undefined) || user.email?.split("@")[0] || "Cliente");

  const handleSignOut = async () => {
    await signOut();
    setAccountOpen(false);
    showToast("Você saiu da conta");
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 bg-background-50 border-b border-background-200/70">
      <div className="w-full px-4 md:px-8 pt-3 md:pt-4 pb-2">
        <div className="flex items-center gap-3 md:gap-5">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 cursor-pointer">
            <h1 className="font-heading text-primary-600 text-3xl md:text-[34px] font-semibold tracking-tight leading-none">
              Casa Flor
            </h1>
          </Link>

          {/* Search bar */}
          <form
            onSubmit={onSearch}
            className="flex-1 flex items-center bg-background-50 border-2 border-foreground-900/85 rounded-full overflow-hidden h-11 md:h-12 max-w-[820px] mx-auto"
          >
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar produtos"
              className="flex-1 h-full px-5 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
            />
            <button
              type="submit"
              aria-label="Buscar"
              className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-primary-500 hover:bg-primary-600 flex items-center justify-center text-background-50 transition-colors cursor-pointer shrink-0"
            >
              <i className="ri-search-line text-[18px]"></i>
            </button>
          </form>

          {/* Right icons */}
          <div className="flex items-center gap-0.5 md:gap-1 shrink-0">
            <Link
              to="/favorites"
              aria-label="Favoritos"
              className="relative w-10 h-10 rounded-full flex items-center justify-center text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className={`${favCount > 0 ? "ri-heart-fill text-primary-500" : "ri-heart-line"} text-[20px]`}></i>
              {favCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-foreground-950 text-background-50 text-[10px] font-semibold flex items-center justify-center">
                  {favCount}
                </span>
              )}
            </Link>

            {/* Account dropdown */}
            <div className="relative hidden sm:block" ref={accountRef}>
              <button
                type="button"
                aria-label="Conta"
                onClick={() => setAccountOpen((v) => !v)}
                className="inline-flex items-center gap-0.5 h-10 px-1 rounded-full text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                    user
                      ? "bg-foreground-950 text-background-50"
                      : "bg-accent-200 text-accent-900"
                  }`}
                >
                  {initial}
                </div>
                <i className="ri-arrow-down-s-line text-[14px]"></i>
              </button>

              {accountOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 rounded-xl bg-background-50 border border-background-200/70 ring-1 ring-background-200/40 overflow-hidden z-50">
                  {user ? (
                    <>
                      <div className="px-4 py-3 border-b border-background-200/60">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-foreground-500 mb-0.5">
                          Conectado como
                        </p>
                        <p className="text-[13.5px] text-foreground-950 font-medium truncate">
                          {displayName}
                        </p>
                        <p className="text-[12px] text-foreground-600 truncate">{user.email}</p>
                      </div>
                      <Link
                        to="/account"
                        onClick={() => setAccountOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-[13.5px] text-foreground-900 hover:bg-background-100 cursor-pointer"
                      >
                        <i className="ri-dashboard-line text-[16px]"></i>
                        Minha conta
                      </Link>
                      <Link
                        to="/account/orders"
                        onClick={() => setAccountOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-[13.5px] text-foreground-900 hover:bg-background-100 cursor-pointer"
                      >
                        <i className="ri-shopping-bag-3-line text-[16px]"></i>
                        Meus pedidos
                      </Link>
                      <Link
                        to="/favorites"
                        onClick={() => setAccountOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-[13.5px] text-foreground-900 hover:bg-background-100 cursor-pointer"
                      >
                        <i className="ri-heart-line text-[16px]"></i>
                        Favoritos
                      </Link>
                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[13.5px] text-foreground-900 hover:bg-background-100 cursor-pointer border-t border-background-200/60"
                      >
                        <i className="ri-logout-box-r-line text-[16px]"></i>
                        Sair
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="px-4 pt-4 pb-3">
                        <p className="text-[14px] text-foreground-950 font-medium">
                          Minha conta
                        </p>
                      </div>
                      <div className="px-4 pb-3 space-y-2">
                        <Link
                          to="/auth"
                          onClick={() => setAccountOpen(false)}
                          className="w-full h-9 inline-flex items-center justify-center rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[13px] font-medium cursor-pointer whitespace-nowrap"
                        >
                          Entrar
                        </Link>
                        <Link
                          to="/auth?mode=signup"
                          onClick={() => setAccountOpen(false)}
                          className="w-full h-9 inline-flex items-center justify-center rounded-full border border-background-300 hover:border-foreground-900 text-foreground-900 text-[13px] font-medium cursor-pointer whitespace-nowrap"
                        >
                          Criar conta
                        </Link>
                      </div>
                      <div className="border-t border-background-200/60">
                        <Link
                          to="/favorites"
                          onClick={() => setAccountOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-[13.5px] text-foreground-900 hover:bg-background-100 cursor-pointer"
                        >
                          <i className="ri-heart-line text-[16px]"></i>
                          Favoritos
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={openCart}
              aria-label="Carrinho"
              className="relative w-10 h-10 rounded-full flex items-center justify-center text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-shopping-cart-2-line text-[20px]"></i>
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary-500 text-background-50 text-[10px] font-semibold flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            <button
              type="button"
              aria-label="Menu"
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden w-10 h-10 rounded-full flex items-center justify-center text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-[20px]`}></i>
            </button>
          </div>
        </div>

        {/* Sub-nav row */}
        <nav
          aria-label="Navegar pelas coleções"
          className="hidden md:flex items-center justify-center gap-7 lg:gap-10 pt-4 pb-3 md:pt-5 md:pb-4"
        >
          {subNav.map((item) => {
            const isActive = location.pathname === item.to;
            return (
              <Link
                key={item.label}
                to={item.to}
                className={`inline-flex items-center gap-1.5 text-[15px] md:text-[15.5px] leading-none transition-colors whitespace-nowrap cursor-pointer pb-1 border-b-2 ${
                  isActive
                    ? "text-foreground-950 border-foreground-950 font-medium"
                    : "text-foreground-900 border-transparent hover:text-primary-600 hover:border-primary-500/60"
                }`}
              >
                {item.icon && <i className={`${item.icon} text-[16px]`}></i>}
                {item.label}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-background-200/60 px-4 py-2 bg-background-50">
          <ul className="flex flex-col">
            {subNav.map((item) => (
              <li key={item.label}>
                <Link
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 py-3 text-[15px] text-foreground-900 border-b border-background-200/50 last:border-0 cursor-pointer"
                >
                  {item.icon && <i className={`${item.icon} text-[16px]`}></i>}
                  {item.label}
                </Link>
              </li>
            ))}
            {user ? (
              <>
                <li>
                  <Link
                    to="/account"
                    onClick={() => setMobileOpen(false)}
                    className="block py-2.5 text-sm text-foreground-900 border-b border-background-200/50 cursor-pointer"
                  >
                    Minha conta
                  </Link>
                </li>
                <li>
                  <Link
                    to="/account/orders"
                    onClick={() => setMobileOpen(false)}
                    className="block py-2.5 text-sm text-foreground-900 border-b border-background-200/50 cursor-pointer"
                  >
                    Meus pedidos
                  </Link>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      setMobileOpen(false);
                      void handleSignOut();
                    }}
                    className="w-full text-left py-2.5 text-sm text-foreground-900 cursor-pointer"
                  >
                    Sair
                  </button>
                </li>
              </>
            ) : (
              <li>
                <Link
                  to="/auth"
                  onClick={() => setMobileOpen(false)}
                  className="block py-2.5 text-sm text-foreground-900 cursor-pointer"
                >
                  Entrar / Criar conta
                </Link>
              </li>
            )}
          </ul>
        </div>
      )}
    </header>
  );
}